import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useAppStore } from '@/lib/store';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';

const Hero: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDarkMode = useAppStore((s) => s.isDarkMode);
  const heroContent = useAdminStore((s) => s.settings?.heroContent);
  const { t, localize } = useTranslation();
  const { scrollY } = useScroll();

  const y = useTransform(scrollY, [0, 800], [0, 300]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);
  const scale = useTransform(scrollY, [0, 1000], [1, 0.9]);

  const rawTitle = heroContent?.title || 'FOTOATELIER';
  const title = rawTitle.endsWith('.') ? rawTitle : rawTitle + '.';
  const subtitle = localize(heroContent?.subtitle) || 'Fine Art Photography Gallery';
  const letters = title.split('');

  return (
    <section
      ref={containerRef}
      className="relative h-screen w-full flex items-center justify-center overflow-hidden bg-white dark:bg-black transition-colors duration-700"
    >
      <div className="absolute inset-0 z-0 opacity-10 grayscale scale-110">
        <video autoPlay muted loop playsInline className="w-full h-full object-cover">
          <source
            src="https://assets.mixkit.co/videos/preview/mixkit-bright-light-refracting-on-white-background-4355-large.mp4"
            type="video/mp4"
          />
        </video>
        <div
          className={`absolute inset-0 transition-colors duration-700 ${isDarkMode ? 'bg-black/60' : 'bg-transparent'}`}
        />
      </div>

      <motion.div
        className="relative z-10 flex flex-col items-center"
        style={{ y, opacity, scale }}
      >
        <div className="flex overflow-hidden">
          {letters.map((letter, i) => (
            <motion.span
              key={`${letter}-${i}`}
              initial={{ y: '100%', rotate: 20 }}
              animate={{ y: 0, rotate: 0 }}
              transition={{
                duration: 1.2,
                delay: i * 0.06,
                ease: [0.76, 0, 0.24, 1],
              }}
              className="text-[10vw] md:text-[8vw] font-syncopate font-bold text-black dark:text-white tracking-tight leading-none inline-block uppercase transition-colors duration-700 logo-brand"
            >
              {letter}
            </motion.span>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, letterSpacing: '1em' }}
          animate={{ opacity: 0.3, letterSpacing: '0.8em' }}
          transition={{ delay: 1, duration: 2 }}
          className="mt-6 text-[9px] md:text-xs font-bold uppercase text-black dark:text-white transition-colors duration-700 logo-brand"
        >
          {subtitle}
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-32 sm:bottom-10 left-0 right-0 flex flex-col items-center gap-4 z-30 opacity-40"
        animate={{ y: [0, 5, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-px h-10 bg-black/10 dark:bg-white/10" />
        <span className="text-[8px] font-bold uppercase tracking-[0.6em] text-black dark:text-white text-center">
          {t.hero.explore}
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
